import type { FC, ReactNode } from 'react'
import type { TagDetail, TagKind, TagPageData, TagWithCount } from '@/types'

import Link from 'next/link'
import { ArrowRight, GraduationCap } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatCount } from '@/lib/format'
import { ENTRY_KINDS, TAG_KINDS, TAG_KIND_ORDER, categoryLabel } from '@/lib/kinds'
import PageHeader from '@/components/kb/page-header'
import EntryCard from '@/components/kb/entry-card'
import QaCard from '@/components/kb/qa-card'
import TagChip from '@/components/kb/tag-chip'
import Pagination from '@/components/kb/pagination'
import EmptyState from '@/components/kb/empty-state'
import { Pill } from '@/components/kb/section-heading'

interface TagDetailViewProps {
    data: TagPageData
}

const RELATED_LABELS: Record<TagKind, string> = {
    samagri: 'Used together with',
    event: 'For events and occasions',
    place: 'In these places',
    goal: 'For these goals',
    topic: 'Topics'
}

const INTRO: Record<TagKind, string> = {
    samagri: 'Every remedy, vidhi, brahmastra and answer that uses this item, with the event, place and goal each one serves.',
    event: 'Everything to do for this occasion: remedies, muhurta rules, methods and the samagri they need.',
    place: 'Every remedy, rule and answer for this direction or space, with the samagri and goals involved.',
    goal: 'Remedies, methods and answers for this goal, with the samagri, places and timings they use.',
    topic: 'Sessions, remedies, methods and answers on this topic across all courses.'
}

const usageTerms = (tag: TagDetail): string[] => {
    const terms = new Set<string>()

    for (const part of [tag.name, ...tag.aliases]) {
        const lower = part.toLowerCase()
        const outer = lower.replace(/\([^)]*\)/g, '').trim()
        const inner = lower.match(/\(([^)]+)\)/)?.[1]?.trim()

        for (const term of [outer, inner]) {
            if (term && term.length >= 4) terms.add(term)
        }
    }

    return [...terms]
}

const groupRelated = (related: TagWithCount[]): { kind: TagKind, tags: TagWithCount[] }[] => {
    return TAG_KIND_ORDER
        .map(kind => ({ kind, tags: related.filter(tag => tag.kind === kind).slice(0, 14) }))
        .filter(group => group.tags.length > 0)
}

const TagDetailView: FC<TagDetailViewProps> = ({ data }): ReactNode => {
    const { tag, kindCounts, entryKind, entries, qa, qaTotal, related, sessions } = data
    const meta = TAG_KINDS[tag.kind]
    const Icon = meta.icon
    const basePath = `${meta.path}/${tag.slug}`
    const totalEntries = kindCounts.reduce((sum, item) => sum + item.count, 0)
    const terms = tag.kind === 'samagri' ? usageTerms(tag) : []
    const relatedGroups = groupRelated(related)

    return (
        <>
            <PageHeader
                crumbs={[{ label: 'Home', href: '/' }, { label: meta.label, href: meta.path }, { label: <span data-mdlfy-cms={`tags:${tag.id}:name`}>{tag.name}</span> }]}
                eyebrow={(
                    <div className='flex flex-wrap items-center gap-2'>
                        <Pill tone={meta.tone}>
                            <Icon className='size-4' aria-hidden='true' />
                            {meta.singular}
                        </Pill>

                        {tag.category && (
                            <span className='rounded-md border-2 border-foreground bg-card px-2.5 py-1 font-mono text-[11px] font-bold uppercase'>
                                {categoryLabel(tag.category)}
                            </span>
                        )}
                    </div>
                )}
                title={<span data-mdlfy-cms={`tags:${tag.id}:name`}>{tag.name}</span>}
                subtitle={tag.name_hi ? <span data-mdlfy-cms={`tags:${tag.id}:name_hi`}>{tag.name_hi}</span> : undefined}
                description={tag.description ? <span data-mdlfy-cms={`tags:${tag.id}:description`}>{tag.description}</span> : INTRO[tag.kind]}
            >
                <div className='flex flex-col gap-4'>
                    <div className='flex flex-wrap gap-3'>
                        <span className='rounded-xl border-2 border-foreground bg-card px-4 py-2 font-mono text-sm font-bold shadow-brutal-sm'>
                            {formatCount(totalEntries)} entries
                        </span>

                        <span className='rounded-xl border-2 border-foreground bg-card px-4 py-2 font-mono text-sm font-bold shadow-brutal-sm'>
                            {formatCount(qaTotal)} questions
                        </span>

                        {sessions.length > 0 && (
                            <span className='rounded-xl border-2 border-foreground bg-card px-4 py-2 font-mono text-sm font-bold shadow-brutal-sm'>
                                {formatCount(sessions.length)} sessions
                            </span>
                        )}
                    </div>

                    {tag.aliases.length > 0 && (
                        <p className='flex flex-wrap items-center gap-2 text-sm'>
                            <span className='font-semibold text-muted-foreground'>Also known as</span>

                            {tag.aliases.slice(0, 10).map(alias => (
                                <span key={alias} className='rounded-md bg-secondary px-2 py-0.5 font-mono text-xs'>
                                    {alias}
                                </span>
                            ))}
                        </p>
                    )}
                </div>
            </PageHeader>

            <div className='mx-auto grid max-w-6xl gap-8 px-4 py-10 sm:px-6 lg:grid-cols-[minmax(0,1fr)_300px]'>
                <div className='min-w-0'>
                    <section id='entries' aria-labelledby='entries-heading'>
                        <h2 id='entries-heading' className='mb-4 text-2xl font-black md:text-3xl'>
                            {tag.kind === 'samagri' ? 'Where it is used' : 'Remedies, methods and rules'}
                        </h2>

                        {kindCounts.length > 0 && (
                            <div className='mb-6 flex flex-wrap gap-2'>
                                <Link
                                    href={`${basePath}#entries`}
                                    className={cn('inline-flex items-center gap-2 rounded-full border-2 border-foreground px-3 py-1 text-sm font-bold', !entryKind ? 'bg-foreground text-background' : 'bg-card hover:bg-primary')}
                                >
                                    All
                                    <span className='font-mono text-xs'>{formatCount(totalEntries)}</span>
                                </Link>

                                {kindCounts.map(item => {
                                    const kindMeta = ENTRY_KINDS.find(value => value.kind === item.kind)

                                    if (!kindMeta) return null

                                    const KindIconComponent = kindMeta.icon

                                    return (
                                        <Link
                                            key={item.kind}
                                            href={`${basePath}?kind=${item.kind}#entries`}
                                            className={cn('inline-flex items-center gap-2 rounded-full border-2 border-foreground px-3 py-1 text-sm font-bold', entryKind === item.kind ? 'bg-foreground text-background' : cn(kindMeta.tone, 'hover:brightness-95'))}
                                        >
                                            <KindIconComponent className='size-4' aria-hidden='true' />
                                            {kindMeta.plural}
                                            <span className='font-mono text-xs'>{formatCount(item.count)}</span>
                                        </Link>
                                    )
                                })}
                            </div>
                        )}

                        {entries.items.length === 0 ? (
                            <EmptyState title='No entries yet' text='Remedies and methods linked to this tag will appear here as more classes are processed.' />
                        ) : (
                            <div className='grid gap-5 md:grid-cols-2'>
                                {entries.items.map(entry => (
                                    <EntryCard key={entry.id} entry={entry} usageTerms={terms} showPlacement={tag.kind !== 'place'} />
                                ))}
                            </div>
                        )}

                        <div className='mt-8'>
                            <Pagination page={entries.page} pageSize={entries.pageSize} total={entries.total} basePath={basePath} params={{ kind: entryKind }} anchor='entries' />
                        </div>
                    </section>

                    {qa.length > 0 && (
                        <section id='questions' aria-labelledby='questions-heading' className='mt-14'>
                            <div className='mb-4 flex flex-wrap items-end justify-between gap-3'>
                                <h2 id='questions-heading' className='text-2xl font-black md:text-3xl'>
                                    Questions and answers
                                </h2>

                                {qaTotal > qa.length && (
                                    <Link href={`/qa?tag=${tag.slug}`} className='inline-flex items-center gap-1 text-sm font-bold underline decoration-2 underline-offset-4'>
                                        All {formatCount(qaTotal)} questions
                                        <ArrowRight className='size-4' aria-hidden='true' />
                                    </Link>
                                )}
                            </div>

                            <div className='flex flex-col gap-3'>
                                {qa.map(item => (
                                    <QaCard key={item.id} qa={item} />
                                ))}
                            </div>
                        </section>
                    )}
                </div>

                <aside className='flex flex-col gap-6'>
                    {relatedGroups.map(group => (
                        <div key={group.kind} className='rounded-xl border-2 border-foreground bg-card p-5 shadow-brutal'>
                            <h2 className='mb-3 text-sm font-black uppercase tracking-wide'>
                                {RELATED_LABELS[group.kind]}
                            </h2>

                            <div className='flex flex-wrap gap-2'>
                                {group.tags.map(item => (
                                    <TagChip key={item.id} tag={item} count={item.entry_count} />
                                ))}
                            </div>
                        </div>
                    ))}

                    {sessions.length > 0 && (
                        <div className='rounded-xl border-2 border-foreground bg-card p-5 shadow-brutal'>
                            <h2 className='mb-3 flex items-center gap-2 text-sm font-black uppercase tracking-wide'>
                                <GraduationCap className='size-4' aria-hidden='true' />
                                Taught in these sessions
                            </h2>

                            <ul className='flex flex-col gap-2.5'>
                                {sessions.slice(0, 24).map(session => (
                                    <li key={session.id}>
                                        <Link href={`/sessions/${session.slug}`} className='block text-sm leading-snug hover:underline'>
                                            <span className='font-semibold' data-mdlfy-cms={`sessions:${session.id}:title`}>{session.title}</span>

                                            {session.course_title && session.course_id && (
                                                <span className='block text-xs text-muted-foreground' data-mdlfy-cms={`courses:${session.course_id}:title`}>
                                                    {session.course_title}
                                                </span>
                                            )}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <Link href={meta.path} className='flex items-center justify-between gap-3 rounded-xl border-2 border-foreground bg-primary p-4 text-sm font-bold shadow-brutal transition-transform hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-brutal-sm'>
                        All {meta.label.toLowerCase()}
                        <ArrowRight className='size-4' aria-hidden='true' />
                    </Link>
                </aside>
            </div>
        </>
    )
}

export default TagDetailView
